// src/pages/admin/invoices/CreateInvoice.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Typography,
} from "@material-tailwind/react";
import { TrashIcon, PrinterIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import {
  collection,
  getDocs,
  getDoc,
  serverTimestamp,
  query,
  where,
  limit as limitFn,
  runTransaction,
  doc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../../firebaseConfig";
import { printInvoiceToTwoPrinters } from "../../../printing/printService";

/* ✅ ألوان الصورة فقط (Grayscale) */
const BRAND = {
  ink: "#2F2F2F",
  graphite: "#878787",
  silver: "#B8B8B8",
  mist: "#D0D0D0",
  paper: "#EFEFEF",
  white: "#FCFCFC",
};

/* رقم عشوائي 8 خانات مع فحص */
function rand8() {
  return Math.floor(10000000 + Math.random() * 90000000);
}
async function getRandomInvoiceNo(uid) {
  for (let i = 0; i < 10; i++) {
    const candidate = rand8();
    const q = query(
      collection(db, "users", uid, "invoices"),
      where("invoiceNo", "==", candidate),
      limitFn(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return candidate;
  }
  throw new Error("تعذّر توليد رقم فاتورة فريد. أعد المحاولة.");
}

export default function CreateInvoice() {
  const [user, setUser] = useState(null);
  const [ownerUid, setOwnerUid] = useState(""); // ✅ هذا هو UID الذي أنشأ المستخدم (createdBy)
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  // نموذج
  const [customerName, setCustomerName] = useState("");
  const [notes, setNotes] = useState("فاتورة بيع");
  const [discount, setDiscount] = useState("");
  const [selectedCat, setSelectedCat] = useState("all");
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState([]); // {productId,name,price,qty,barcode}

  const ui = useMemo(() => {
    return {
      pageBg: `radial-gradient(1200px 520px at 90% -30%, ${BRAND.paper}, ${BRAND.white} 55%)`,
      cardBorder: `1px solid ${BRAND.paper}`,
      cardShadow: "0 18px 50px rgba(0,0,0,0.06), 0 6px 18px rgba(0,0,0,0.04)",
      headerBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      btnBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      ring: "0 0 0 4px rgba(135,135,135,0.18)",
    };
  }, []);

  const inputCls =
    "w-full rounded-2xl border px-3 py-3 outline-none transition font-tajawal bg-white";
  const btnPrimary =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold transition disabled:opacity-60";

  // ✅ 1) نقرأ المستخدم + وثيقته ونستخرج createdBy
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      setOwnerUid("");
      setProducts([]);
      setCats([]);
      setErr("");
      setOk("");

      if (!u?.uid) return;

      try {
        // نفترض وثيقة المستخدم موجودة في users/{uid}
        const uRef = doc(db, "users", u.uid);
        const uSnap = await getDoc(uRef);
        const data = uSnap.exists() ? uSnap.data() || {} : {};

        // ✅ إذا ماكو createdBy نخليها uid نفسه
        const creator = data.createdBy || u.uid;
        setOwnerUid(creator);
      } catch (e) {
        console.error(e);
        // fallback
        setOwnerUid(u.uid);
      }
    });

    return () => unsub();
  }, []);

  // ✅ 2) جلب البيانات: المنتجات من ownerUid بدل user.uid
  useEffect(() => {
    if (!user?.uid || !ownerUid) return;

    (async () => {
      setLoading(true);
      setErr("");
      setOk("");
      try {
        const catSnap = await getDocs(collection(db, "categories"));
        const cats_ = catSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        cats_.sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), "ar")
        );

        // ✅ هنا التغيير المهم
        const prodSnap = await getDocs(collection(db, "users", ownerUid, "products"));
        const prods = prodSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

        setCats(cats_);
        setProducts(prods);
      } catch (e) {
        console.error(e);
        setErr("تعذّر جلب البيانات.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, ownerUid]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      const catOk = selectedCat === "all" ? true : p.categoryId === selectedCat;
      const sOk = q
        ? String(p.name || "").toLowerCase().includes(q) ||
          String(p.code || "").toLowerCase().includes(q) ||
          String(p.barcode || "").toLowerCase().includes(q)
        : true;
      return catOk && sOk;
    });
  }, [products, selectedCat, search]);

  function addLine(p) {
    if (!p) return;
    setLines((prev) => {
      const i = prev.findIndex((x) => x.productId === p.id);
      if (i > -1) {
        const copy = prev.slice();
        copy[i] = { ...copy[i], qty: copy[i].qty + 1 };
        return copy;
      }
      return [
        ...prev,
        {
          productId: p.id,
          name: p.name || "-",
          price: +p.price || 0,
          qty: 1,
          barcode: p.barcode || p.code || "",
        },
      ];
    });
  }

  function removeLine(productId) {
    setLines((prev) => prev.filter((x) => x.productId !== productId));
  }

  function setQty(productId, qty) {
    setLines((prev) =>
      prev.map((x) =>
        x.productId === productId ? { ...x, qty: Math.max(1, +qty || 1) } : x
      )
    );
  }

  function setPrice(productId, price) {
    setLines((prev) =>
      prev.map((x) =>
        x.productId === productId ? { ...x, price: Math.max(0, +price || 0) } : x
      )
    );
  }

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + l.price * l.qty, 0),
    [lines]
  );
  const discountNum = Math.min(100, Math.max(0, +discount || 0));
  const total = useMemo(
    () => Math.max(0, subtotal - (subtotal * discountNum) / 100),
    [subtotal, discountNum]
  );

  async function performSave() {
    if (!user?.uid) throw new Error("الرجاء تسجيل الدخول.");
    if (!ownerUid) throw new Error("تعذّر تحديد مالك المنتجات (createdBy).");
    if (!lines.length) throw new Error("أضف عناصر للفاتورة.");

    const invoiceNo = await getRandomInvoiceNo(user.uid);

    const payload = {
      invoiceNo,
      customerName: customerName.trim() || "",
      notes: notes.trim() || "",
      discount: discountNum,
      subtotal,
      total,
      // ✅ نخزن ownerUid حتى تعرف الفاتورة من أي مخزون
      ownerUid,
      items: lines.map((l) => ({
        productId: l.productId,
        name: l.name,
        price: l.price,
        qty: l.qty,
        barcode: l.barcode,
      })),
      createdAt: serverTimestamp(),
      cashierId: user.uid,
    };

    const soldById = new Map();
    for (const l of lines) {
      soldById.set(l.productId, (soldById.get(l.productId) || 0) + (+l.qty || 0));
    }

    await runTransaction(db, async (tx) => {
      const readResults = [];

      // ✅ قراءة المنتجات من حساب المنشئ (ownerUid)
      for (const [productId, soldQty] of soldById.entries()) {
        const pRef = doc(db, "users", ownerUid, "products", productId);
        const snap = await tx.get(pRef);
        if (!snap.exists()) throw new Error(`المنتج غير موجود (ID: ${productId}).`);
        readResults.push({ ref: pRef, snap, soldQty: +soldQty || 0 });
      }

      for (const { ref, snap, soldQty } of readResults) {
        const curQty = +snap.data()?.quantity || 0;
        tx.update(ref, { quantity: Math.max(0, curQty - soldQty) });
      }

      // ✅ حفظ الفاتورة تبقى على المستخدم الحالي (الكاشير)
      const invRef = doc(collection(db, "users", user.uid, "invoices"));
      tx.set(invRef, payload);

      const cashRef = doc(collection(db, "users", user.uid, "cashbox"));
      tx.set(cashRef, {
        kind: "receipt",
        source: "invoice",
        invoiceId: invRef.id,
        invoiceNo,
        amount: total,
        note: notes || "",
        createdAt: serverTimestamp(),
      });
    });

    // ✅ تحديث الواجهة (المنتجات التي كانت معروضة أصلاً هي منتجات ownerUid)
    setProducts((prev) =>
      prev.map((p) => {
        const sold = soldById.get(p.id) || 0;
        if (!sold) return p;
        const curQty = +p.quantity || 0;
        return { ...p, quantity: Math.max(0, curQty - sold) };
      })
    );

    return invoiceNo;
  }

  function clearForm() {
    setLines([]);
    setCustomerName("");
    setNotes("فاتورة بيع");
    setDiscount("");
  }

  async function saveAndPrint() {
    if (saving) return;
    setErr("");
    setOk("");
    try {
      setSaving(true);
      const invoiceNo = await performSave();

      await printInvoiceToTwoPrinters({
        invoiceNo,
        lines,
        subtotal,
        discount: discountNum,
        total,
        customerName,
        brand: BRAND,
      });

      setOk(`تم حفظ الفاتورة رقم ${invoiceNo} وإرسالها للطابعتين`);
      clearForm();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "تعذّر حفظ/طباعة الفاتورة.");
    } finally {
      setSaving(false);
    }
  }

  function goBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  }

  return (
    <div dir="rtl" className="p-4 min-h-screen" style={{ background: ui.pageBg }}>
      <Card
        className="mx-auto max-w-7xl rounded-3xl overflow-hidden bg-white"
        style={{ border: ui.cardBorder, boxShadow: ui.cardShadow }}
      >
        <CardHeader floated={false} shadow={false} className="p-0">
          <div
            className="flex items-center justify-between px-6 py-6"
            style={{ background: ui.headerBg }}
          >
            <button
              onClick={goBack}
              className="group inline-flex items-center gap-2 rounded-2xl px-3 py-2 transition"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: BRAND.white,
                fontFamily: "Tajawal, system-ui, sans-serif",
              }}
              title="رجوع"
            >
              <ArrowRightIcon className="h-5 w-5" />
              <span className="text-sm font-extrabold">رجوع</span>
            </button>

            <div className="text-center flex-1">
              <Typography variant="h4" className="text-white font-extrabold font-tajawal">
                قطع فاتورة
              </Typography>
            </div>

            <span className="w-[92px]" />
          </div>
        </CardHeader>

        <CardBody className="space-y-4 p-6">
          {(err || ok) && (
            <div className="space-y-2">
              {err && (
                <div
                  className="rounded-2xl px-4 py-3 font-tajawal"
                  style={{
                    background: BRAND.paper,
                    border: `1px solid ${BRAND.mist}`,
                    color: BRAND.ink,
                  }}
                >
                  {err}
                </div>
              )}
              {ok && (
                <div
                  className="rounded-2xl px-4 py-3 font-tajawal"
                  style={{
                    background: BRAND.white,
                    border: `1px solid ${BRAND.mist}`,
                    color: BRAND.ink,
                  }}
                >
                  {ok}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* كتالوج */}
            <div className="rounded-3xl border p-4" style={{ borderColor: BRAND.paper }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label
                    className="mb-2 block text-sm font-extrabold font-tajawal"
                    style={{ color: BRAND.ink }}
                  >
                    بحث
                  </label>
                  <input
                    className={inputCls}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="ابحث بالاسم/الكود/الباركود"
                    style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                    onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                    onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                  />
                </div>

                <div>
                  <label
                    className="mb-2 block text-sm font-extrabold font-tajawal"
                    style={{ color: BRAND.ink }}
                  >
                    الفئة
                  </label>
                  <select
                    className={inputCls}
                    value={selectedCat}
                    onChange={(e) => setSelectedCat(e.target.value)}
                    style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                    onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                    onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                  >
                    <option value="all">كل الفئات</option>
                    {cats.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 mt-4 max-h-[380px] overflow-auto">
                {filteredProducts.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => addLine(p)}
                    className="text-right border rounded-2xl p-3 transition"
                    style={{
                      borderColor: BRAND.paper,
                      background: BRAND.white,
                      boxShadow: "0 8px 18px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div className="text-sm font-extrabold line-clamp-2 font-tajawal" style={{ color: BRAND.ink }}>
                      {p.name}
                    </div>
                    <div className="text-xs mt-1 font-tajawal" style={{ color: BRAND.graphite }}>
                      السعر: {Number(p.price || 0).toLocaleString("en-EG")} د.ع
                    </div>
                    <div className="text-[11px] mt-1 font-tajawal" style={{ color: BRAND.silver }}>
                      المتوفر: {Number(p.quantity || 0)}
                    </div>
                  </button>
                ))}

                {!filteredProducts.length && (
                  <div className="col-span-full text-center" style={{ color: BRAND.graphite }}>
                    {loading ? "جارٍ التحميل..." : "لا توجد نتائج."}
                  </div>
                )}
              </div>
            </div>

            {/* السلة */}
            <div className="rounded-3xl border p-4 bg-white" style={{ borderColor: BRAND.paper }}>
              <div className="grid grid-cols-1 gap-2">
                <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                  اسم الزبون
                </label>
                <input
                  className={inputCls}
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                  placeholder="اختياري"
                />
              </div>

              <div className="mt-3 border rounded-2xl overflow-hidden" style={{ borderColor: BRAND.paper }}>
                <table className="w-full text-right font-tajawal">
                  <thead style={{ backgroundColor: BRAND.paper }}>
                    <tr>
                      <th className="p-3 text-xs font-extrabold" style={{ color: BRAND.ink }}>
                        المنتج
                      </th>
                      <th className="p-3 text-xs font-extrabold w-24" style={{ color: BRAND.ink }}>
                        الكمية
                      </th>
                      <th className="p-3 text-xs font-extrabold w-28" style={{ color: BRAND.ink }}>
                        السعر
                      </th>
                      <th className="p-3 text-xs font-extrabold w-28" style={{ color: BRAND.ink }}>
                        المجموع
                      </th>
                      <th className="p-3 text-xs font-extrabold w-20" style={{ color: BRAND.ink }}>
                        حذف
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {lines.map((l, idx) => (
                      <tr
                        key={l.productId}
                        className="border-t"
                        style={{
                          borderColor: BRAND.paper,
                          background: idx % 2 === 0 ? BRAND.white : "rgba(239,239,239,0.30)",
                        }}
                      >
                        <td className="p-3 text-sm">{l.name}</td>

                        <td className="p-3">
                          <input
                            className="w-20 rounded-xl border px-2 py-2 text-center outline-none font-tajawal"
                            style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                            type="number"
                            value={l.qty}
                            onChange={(e) => setQty(l.productId, e.target.value)}
                            onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                            onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                          />
                        </td>

                        <td className="p-3">
                          <input
                            className="w-24 rounded-xl border px-2 py-2 text-center outline-none font-tajawal"
                            style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                            type="number"
                            value={l.price}
                            onChange={(e) => setPrice(l.productId, e.target.value)}
                            onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                            onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                          />
                        </td>

                        <td className="p-3 text-sm font-extrabold" style={{ color: BRAND.ink }}>
                          {(Number(l.price) * Number(l.qty)).toLocaleString("en-EG")}
                        </td>

                        <td className="p-3">
                          <button
                            className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm font-extrabold transition"
                            style={{
                              background: BRAND.paper,
                              border: `1px solid ${BRAND.mist}`,
                              color: BRAND.ink,
                            }}
                            onClick={() => removeLine(l.productId)}
                            title="حذف"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}

                    {!lines.length && (
                      <tr>
                        <td colSpan={5} className="p-4 text-center font-tajawal" style={{ color: BRAND.graphite }}>
                          السلة فارغة
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-tajawal" style={{ color: BRAND.graphite }}>
                    المجموع
                  </span>
                  <span className="font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                    {subtotal.toLocaleString("en-EG")} د.ع
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-tajawal" style={{ color: BRAND.graphite }}>
                    الخصم (%)
                  </span>
                  <input
                    className="w-24 rounded-xl border px-2 py-2 text-center outline-none font-tajawal"
                    style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                    type="number"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                    onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                    placeholder="0"
                  />
                </div>

                <div className="flex items-center justify-between text-lg">
                  <span className="font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                    الإجمالي
                  </span>
                  <span className="font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                    {total.toLocaleString("en-EG")} د.ع
                  </span>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  className={btnPrimary + " w-full"}
                  onClick={saveAndPrint}
                  disabled={saving || !lines.length}
                  style={{
                    background: ui.btnBg,
                    color: BRAND.white,
                    boxShadow: "0 12px 28px rgba(0,0,0,0.10)",
                    fontFamily: "Tajawal, system-ui, sans-serif",
                  }}
                >
                  <PrinterIcon className="h-5 w-5" />
                  {saving ? "جارٍ الحفظ..." : "حفظ وطباعة مباشرة"}
                </button>
              </div>
            </div>
          </div>
        </CardBody>

        <CardFooter className="p-4">
          <span className="sr-only">footer</span>
        </CardFooter>
      </Card>
    </div>
  );
}
